import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import AuroraBackground from '../components/AuroraBackground/AuroraBackground';
import Footer from '../components/Footer';
import LanguageSwitcher from '../components/LanguageSwitcher/LanguageSwitcher';
import MetaHead from '../components/MetaHead';
import { storage } from '../utils/platform';
import { API_BASE } from '../config/api';
import { RESTO_TYPE_OPTIONS } from '../constants/restoTypes';
import { translateApiError } from '../utils/errors';
import { PLAN_BY_ID, PLAN_IDS } from '../constants/plans';
import { formatPrice } from '../utils/format';
import { isNative } from '../utils/platform';

// ═════════════════════════════════════════════════════════════════════════
// ⚠️ RÈGLE ANTI-RÉGRESSION — i18n Phase 4
// Logique fonctionnelle 100% préservée par rapport à la version Aurora P2 + hotfix CGV.
// Préservés identiques :
//  - 16 champs du state `form` (prenom, nom, email, password, nom_restaurant,
//    ville, telephone, decision_maker, code_postal, lat, lng, type_cuisine,
//    google_place_id, google_maps_url, note_depart, avis_depart)
//  - States annexes : confirmPassword, cgvAccepted, error, loading,
//    ville suggestions (2), Google Places (5)
//  - Handlers identiques : chercherGoogle (route publique /api/public/places/search),
//    selectGooglePlace, handleChange, handleVilleInput (API api-adresse.data.gouv),
//    selectVille, handleRegister (toutes validations incluant CGV,
//    body cgv_accepted: true + cgv_version, parse défensif, storage.set token)
//  - Stepper visuel 3 dots, checkbox CGV bloquante (hotfix 22 avril)
// Ajout P4 : envoi de `lang: i18n.language` au backend pour email de bienvenue localisé.
// ═════════════════════════════════════════════════════════════════════════

// TYPES_ETABLISSEMENT vient de constants/restoTypes.js (source unique avec
// l'onglet Configuration). Garde l'ancien nom pour ne pas toucher au reste
// du composant Register.
const TYPES_ETABLISSEMENT = RESTO_TYPE_OPTIONS;

export default function Register() {
  const { t, i18n } = useTranslation('auth');
  const [form, setForm] = useState({
    prenom: '', nom: '', email: '', password: '', nom_restaurant: '', ville: '', telephone: '', decision_maker: '',
    code_postal: '', lat: null, lng: null,
    type_cuisine: '',
    google_place_id: '', google_maps_url: '', note_depart: null, avis_depart: null
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [villeSuggestions, setVilleSuggestions] = useState([]);
  const [villeContexte, setVilleContexte] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [googleQuery, setGoogleQuery] = useState('');
  const [googleResults, setGoogleResults] = useState([]);
  const [googleSearching, setGoogleSearching] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const navigate = useNavigate();

  // Bug 2 audit UX — lecture du `?plan=` venant de /tarifs.
  // Whitelist côté front (`PLAN_IDS`) : valeur invalide → null = état neutre
  // (récap masqué, page rendue comme avant). Le back appliquera sa propre
  // whitelist serveur en défense en profondeur.
  const [searchParams] = useSearchParams();
  const rawPlan = searchParams.get('plan');
  // On accepte les plans de la grille publique (PLAN_IDS) ET les plans legacy
  // encore présents dans PLAN_META (ex: un vieux lien /register?plan=essentiel
  // en circulation) — sinon l'intention du visiteur serait silencieusement
  // perdue. Le backend applique la même tolérance (auth.js VALID_PLANS).
  const selectedPlan = (rawPlan && PLAN_BY_ID[rawPlan]) ? rawPlan : null;
  const planMeta = selectedPlan ? PLAN_BY_ID[selectedPlan] : null;

  // Campagne prospection — provenance de la page /audit/:placeId
  // (?from=audit&place_id=XXX). Lu une seule fois au mount : l'URL est
  // prioritaire, sinon on retombe sur localStorage (résiste à un refresh ou à
  // un retour sur /register sans query). Le backend (auth.js) re-whitelist
  // signup_source en défense en profondeur et tronque place_id.
  const [signupAttribution] = useState(() => {
    let source = searchParams.get('from');
    let placeId = searchParams.get('place_id');
    try {
      if (source) localStorage.setItem('revyo_signup_source', source);
      else source = localStorage.getItem('revyo_signup_source');
      if (placeId) localStorage.setItem('revyo_signup_place_id', placeId);
      else placeId = localStorage.getItem('revyo_signup_place_id');
    } catch (e) { /* localStorage indispo (mode privé) — non bloquant */ }
    return { source: source || null, placeId: placeId || null };
  });

  const chercherGoogle = async () => {
    if (!googleQuery.trim()) return;
    setGoogleSearching(true);
    setGoogleResults([]);
    setGoogleError('');
    try {
      // Route publique (pas de JWT) — l'utilisateur n'est pas encore connecté à ce stade.
      const r = await fetch(`${API_BASE}/api/public/places/search?q=` + encodeURIComponent(googleQuery) + '&ville=' + encodeURIComponent(form.ville || '') + '&lang=' + encodeURIComponent(i18n.language || 'fr'));
      const data = await r.json();
      if (!r.ok || data.error) {
        setGoogleError(data.error || t('register.google_error_default'));
        setGoogleResults([]);
      } else if ((data.results || []).length === 0) {
        setGoogleError(t('register.google_error_no_results'));
        setGoogleResults([]);
      } else {
        setGoogleResults(data.results);
      }
    } catch (e) {
      setGoogleError(t('register.google_error_network'));
      setGoogleResults([]);
    }
    setGoogleSearching(false);
  };

  const selectGooglePlace = (p) => {
    setForm(prev => ({
      ...prev,
      google_place_id: p.place_id,
      google_maps_url: p.google_maps_url || '',
      note_depart: p.note,
      avis_depart: p.nb_avis
    }));
    setGoogleConnected(true);
    setGoogleResults([]);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleVilleInput = async (e) => {
    const q = e.target.value;
    setForm(prev => ({ ...prev, ville: q, code_postal: '', lat: null, lng: null }));
    setVilleContexte('');
    if (q.length < 2) {
      setVilleSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const lang = i18n.language || 'fr';
      const r = await fetch(`${API_BASE}/api/public/places/cities/autocomplete?q=${encodeURIComponent(q)}&lang=${encodeURIComponent(lang)}`);
      if (!r.ok) {
        setVilleSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      const data = await r.json();
      setVilleSuggestions(data.results || []);
      setShowSuggestions(true);
    } catch {
      setVilleSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectVille = (suggestion) => {
    // Google Places Autocomplete (New) ne renvoie pas lat/lng/postcode directement —
    // ils seront enrichis via Place Details lors de la recherche d'établissement.
    setForm(prev => ({
      ...prev,
      ville: suggestion.ville || '',
      code_postal: '',
      lat: null,
      lng: null
    }));
    setVilleContexte(suggestion.contexte || '');
    setShowSuggestions(false);
  };

  const handleRegister = async () => {
    if (!form.prenom || !form.email || !form.password || !form.nom_restaurant) {
      setError(t('register.error_required_fields'));
      return;
    }
    if (!form.type_cuisine) {
      setError(t('register.error_type_required'));
      return;
    }
    if (form.password !== confirmPassword) {
      setError(t('register.error_password_mismatch'));
      return;
    }
    if (!cgvAccepted) {
      setError(t('register.error_cgv_required'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      // 1. Créer le compte
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email, password: form.password, prenom: form.prenom, nom: form.nom,
          nom_restaurant: form.nom_restaurant, ville: form.ville,
          code_postal: form.code_postal, lat: form.lat, lng: form.lng,
          decision_maker: form.decision_maker,
          type_cuisine: form.type_cuisine,
          google_place_id: form.google_place_id,
          google_maps_url: form.google_maps_url,
          note_depart: form.note_depart,
          avis_depart: form.avis_depart,
          // Acceptation CGV — le backend (auth.js) exige strict true, sinon renvoie cgv_required.
          // cgv_version est informationnel côté front ; le backend utilise sa propre CGV_VERSION.
          cgv_accepted: true,
          cgv_version: 'v1-2026-04-20',
          // i18n P4 : transmet la langue active au backend pour localiser l'email
          // de bienvenue. Le backend whitelist (['fr','en','es','it']) avec fallback FR.
          lang: i18n.language || 'fr',
          // Bug 2 audit UX : plan choisi via /tarifs (whitelist front + back).
          // null si aucun plan ou plan invalide — le back stocke null sans erreur.
          plan: selectedPlan,
          // Campagne prospection : attribution de la source d'inscription
          // (page /audit). undefined si absent → champ omis du body, le back stocke null.
          signup_source: signupAttribution.source || undefined,
          signup_place_id: signupAttribution.placeId || undefined
        })
      });

      // Parse défensif : un 502/504 peut renvoyer du HTML et faire exploser res.json().
      let data = {};
      try {
        data = await res.json();
      } catch (parseErr) {
        console.error('Register: réponse non-JSON du backend', parseErr, 'status:', res.status);
      }

      if (!res.ok || !data.token) {
        console.error('Register: échec inscription', { status: res.status, data });
        // i18n P6 : codes backend (email_already_used, duplicate_active, cgv_required, ...)
        // → localisation via translateApiError. Fallback message HTTP générique si code inconnu.
        setError(translateApiError(data) || t('register.error_signup_failed', { status: res.status }));
        setLoading(false);
        return;
      }

      // 2. Stocker le token via le helper storage (Preferences natif sur iOS/Android,
      // localStorage en web). Le verify defensive d'origine (Session 1) ciblait un cas
      // de quota localStorage saturé — devenu inutile avec storage async + Preferences.
      await storage.set('token', data.token);

      // 3. Parcours d'acquisition (décision produit du 30/07/2026) :
      //    - inscription AVEC un plan choisi (/register?plan=pro, cartes de la
      //      grille) → on envoie directement au paiement. Souscrire, c'est payer :
      //      le dashboard n'est pas ouvert avant.
      //    - inscription SANS plan (CTA « Commencer gratuitement ») → essai
      //      gratuit 14 jours au périmètre Découverte, dashboard accessible.
      //
      //    Si la création de session Stripe échoue (price ID absent, réseau,
      //    Stripe indisponible), on NE bloque PAS l'utilisateur sur un écran mort :
      //    son compte existe déjà en essai, on le route vers /abonnement où il
      //    pourra relancer le paiement. Perdre l'inscription serait pire.
      if (selectedPlan) {
        try {
          const checkoutRes = await fetch(`${API_BASE}/api/stripe/create-checkout-session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${data.token}`,
            },
            body: JSON.stringify({ plan: selectedPlan }),
          });
          const checkoutData = await checkoutRes.json().catch(() => ({}));
          if (checkoutRes.ok && checkoutData.url) {
            window.location.href = checkoutData.url;
            return;
          }
          console.error('Register: création session Stripe échouée', checkoutRes.status, checkoutData);
        } catch (checkoutErr) {
          console.error('Register: exception création session Stripe', checkoutErr);
        }
        navigate('/abonnement?checkout=failed');
        return;
      }

      navigate('/dashboard');
    } catch (err) {
      console.error('Register: exception dans handleRegister', err);
      setError(t('register.error_connection'));
      setLoading(false);
    }
  };

  // Indicateur de progression — complètement visuel, pas de logique de
  // "steps" qui bloquerait le submit. Calcule juste un état "rempli / non"
  // sur 3 groupes pour guider l'œil.
  const step1Done = !!form.nom_restaurant && !!form.ville && !!form.type_cuisine;
  const step2Done = !!form.prenom && !!form.email && !!form.password && form.password === confirmPassword && confirmPassword.length > 0;
  const step3Done = cgvAccepted;

  return (
    <>
      <MetaHead page="auth_register" />
      <AuroraBackground dim />
      <style>{css}</style>
      <div className="auth-page">
        {!isNative() && (
        <header className="auth-nav">
          <Link to="/" className="auth-nav-logo">
            <div className="auth-logo-square">R</div>
            <span className="auth-logo-name">Revyo</span>
          </Link>
          <div className="auth-nav-right">
            <LanguageSwitcher compact />
            <Link to="/login" className="auth-nav-cta">{t('common.nav_existing_account')}</Link>
          </div>
        </header>
        )}

        <main className="auth-split">
          <div className="auth-form-col">
            <div className="auth-form-inner auth-form-register">
              <h1 className="auth-title">{t('register.title')}</h1>
              <p className="auth-subtitle">{t('register.subtitle')}</p>

              {planMeta && (
                <div className="auth-plan-recap" role="status">
                  <span className="auth-plan-recap-icon" aria-hidden="true">{planMeta.emoji}</span>
                  <div className="auth-plan-recap-text">
                    <div className="auth-plan-recap-title">
                      <Trans
                        i18nKey="auth:register.plan_recap_title"
                        values={{ planName: t(`register.plan_recap_names.${selectedPlan}`), price: formatPrice(planMeta.price, i18n.language) }}
                        components={{ strong: <strong /> }}
                      />
                    </div>
                    <div className="auth-plan-recap-sub">{t('register.plan_recap_sub')}</div>
                  </div>
                </div>
              )}

              {/* Indicateur de progression visuel (3 dots) */}
              <div className="auth-steps" aria-hidden="true">
                <span className={`auth-step ${step1Done ? 'done' : 'current'}`}>
                  <span className="auth-step-dot" />
                  <span className="auth-step-label">{t('register.step_restaurant')}</span>
                </span>
                <span className={`auth-step ${step1Done ? (step2Done ? 'done' : 'current') : ''}`}>
                  <span className="auth-step-dot" />
                  <span className="auth-step-label">{t('register.step_account')}</span>
                </span>
                <span className={`auth-step ${step2Done ? (step3Done ? 'done' : 'current') : ''}`}>
                  <span className="auth-step-dot" />
                  <span className="auth-step-label">{t('register.step_validation')}</span>
                </span>
              </div>

              {error && <div className="auth-error" role="alert">{error}</div>}

              {/* ═ Section 1 — Restaurant ═ */}
              <div className="auth-section-label">{t('register.section_restaurant')}</div>

              <div className="auth-field">
                <label htmlFor="reg-nom-resto">{t('register.field_restaurant_name_label')}</label>
                <input id="reg-nom-resto" name="nom_restaurant" placeholder={t('register.field_restaurant_name_placeholder')} value={form.nom_restaurant} onChange={handleChange} />
              </div>

              <div className="auth-field auth-field-ville">
                <label htmlFor="reg-ville">{t('register.field_city_label')}</label>
                <input
                  id="reg-ville"
                  name="ville"
                  placeholder={t('register.field_city_placeholder')}
                  value={form.ville}
                  onChange={handleVilleInput}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  onFocus={() => { if (villeSuggestions.length > 0) setShowSuggestions(true); }}
                  autoComplete="off"
                />
                {showSuggestions && villeSuggestions.length > 0 && (
                  <div className="auth-suggestions" role="listbox">
                    {villeSuggestions.map((s) => (
                      <div
                        key={s.place_id}
                        className="auth-suggestion"
                        role="option"
                        onMouseDown={() => selectVille(s)}
                      >
                        <span>{s.ville}</span>
                        {s.contexte && <span className="auth-suggestion-context">{s.contexte}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {form.ville && villeContexte && !showSuggestions && (
                  <div className="auth-field-hint">✓ {form.ville} · {villeContexte}</div>
                )}
              </div>

              <div className="auth-field">
                <label htmlFor="reg-type">{t('register.field_type_label')}</label>
                <select id="reg-type" name="type_cuisine" value={form.type_cuisine} onChange={handleChange}>
                  <option value="">{t('register.field_type_placeholder')}</option>
                  {TYPES_ETABLISSEMENT.map(tt => (
                    <option key={tt.value} value={tt.value}>{tt.label}</option>
                  ))}
                </select>
              </div>

              {/* ═ Section Google ═ */}
              <div className="auth-section-label">{t('register.section_google')}</div>
              <p className="auth-field-hint-top">{t('register.google_helper')}</p>

              {googleConnected ? (
                <div className="auth-google-connected">
                  <span>✅ <strong>{t('register.google_connected_label')}</strong> — {t('register.google_connected_meta', { count: form.avis_depart || 0, note: form.note_depart || '—' })}</span>
                  <button type="button" className="auth-link-btn" onClick={() => {
                    setForm(prev => ({ ...prev, google_place_id: '', google_maps_url: '', note_depart: null, avis_depart: null }));
                    setGoogleConnected(false);
                  }}>{t('register.google_modify')}</button>
                </div>
              ) : (
                <>
                  <div className="auth-field">
                    <label htmlFor="reg-google-query">{t('register.google_field_label')}</label>
                    <input
                      id="reg-google-query"
                      type="text"
                      value={googleQuery}
                      onChange={e => setGoogleQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), chercherGoogle())}
                      placeholder={t('register.google_field_placeholder')}
                    />
                  </div>
                  <button type="button" className="auth-btn-secondary" onClick={chercherGoogle} disabled={googleSearching || !googleQuery.trim()}>
                    {googleSearching ? t('register.google_searching') : t('register.google_search_btn')}
                  </button>
                  {googleResults.length > 0 && (
                    <div className="auth-google-results">
                      {googleResults.map(p => (
                        <div key={p.place_id} className="auth-google-result" onClick={() => selectGooglePlace(p)}>
                          <div className="auth-google-name">{p.nom}</div>
                          <div className="auth-google-addr">{p.adresse}</div>
                          <div className="auth-google-meta">
                            {p.note ? <span>⭐ {p.note}</span> : <span className="auth-muted">{t('register.google_no_rating')}</span>}
                            <span className="auth-muted">{t('register.google_review_count', { count: p.nb_avis })}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!googleSearching && googleError && (
                    <div className="auth-google-err">{googleError}</div>
                  )}
                  <div className="auth-skip">
                    <span onClick={() => setGoogleConnected(true)}>{t('register.google_skip')}</span>
                  </div>
                </>
              )}

              {/* ═ Section 2 — Vous ═ */}
              <div className="auth-section-label">{t('register.section_account')}</div>

              <div className="auth-field-row">
                <div className="auth-field">
                  <label htmlFor="reg-prenom">{t('register.field_firstname_label')}</label>
                  <input id="reg-prenom" name="prenom" placeholder={t('register.field_firstname_placeholder')} value={form.prenom} onChange={handleChange} autoComplete="given-name" />
                </div>
                <div className="auth-field">
                  <label htmlFor="reg-nom">{t('register.field_lastname_label')}</label>
                  <input id="reg-nom" name="nom" placeholder={t('register.field_lastname_placeholder')} value={form.nom} onChange={handleChange} autoComplete="family-name" />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="reg-email">{t('register.field_email_label')}</label>
                <input id="reg-email" name="email" type="email" placeholder={t('register.field_email_placeholder')} value={form.email} onChange={handleChange} autoComplete="email" />
              </div>

              <div className="auth-field">
                <label htmlFor="reg-password">{t('register.field_password_label')}</label>
                <input id="reg-password" name="password" type="password" placeholder={t('register.field_password_placeholder')} value={form.password} onChange={handleChange} autoComplete="new-password" />
              </div>

              <div className="auth-field">
                <label htmlFor="reg-confirm-password">{t('register.field_password_confirm_label')}</label>
                <input id="reg-confirm-password" type="password" placeholder={t('register.field_password_confirm_placeholder')} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" />
              </div>

              <div className="auth-field">
                <label htmlFor="reg-decision">{t('register.field_decision_label')}</label>
                <select id="reg-decision" name="decision_maker" value={form.decision_maker} onChange={handleChange}>
                  <option value="">{t('register.field_decision_placeholder')}</option>
                  <option value="solo">{t('register.field_decision_solo')}</option>
                  <option value="shared">{t('register.field_decision_shared')}</option>
                  <option value="na">{t('register.field_decision_na')}</option>
                </select>
              </div>

              {/* ═ Section 3 — CGV (hotfix 22 avril, CRITIQUE) ═ */}
              <div className="auth-cgv">
                <label className="auth-cgv-label">
                  <input
                    type="checkbox"
                    checked={cgvAccepted}
                    onChange={(e) => setCgvAccepted(e.target.checked)}
                    required
                  />
                  <span>
                    {t('register.cgv_checkbox_prefix')}{' '}
                    <a href="/cgv" target="_blank" rel="noopener noreferrer">{t('register.cgv_link')}</a>
                    {' '}{t('register.cgv_and')}{' '}
                    <a href="/politique-de-confidentialite" target="_blank" rel="noopener noreferrer">{t('register.privacy_link')}</a>.
                  </span>
                </label>
                <div className="auth-cgv-note">
                  {t('register.cgv_note')}{' '}
                  <a href="/politique-de-confidentialite" target="_blank" rel="noopener noreferrer">
                    {t('register.cgv_note_link')}
                  </a>.
                </div>
              </div>

              <button className="auth-btn" onClick={handleRegister} disabled={loading || !cgvAccepted}>
                {loading ? t('register.submitting') : t('register.submit')}
              </button>
              {!cgvAccepted && (
                <div className="auth-cgv-hint">{t('register.cgv_hint')}</div>
              )}

              <p className="auth-switch">
                {t('register.existing_account_line')} <Link to="/login" className="auth-link-primary">{t('register.existing_account_cta')}</Link>
              </p>
            </div>
          </div>
        </main>
      </div>
      {!isNative() && <Footer />}
    </>
  );
}

// ════════════════════════════════════════════════════════════
// CSS — Dark aurora (Phase 2). Palette alignée AuroraBackground/Navbar/Landing v5.
// ════════════════════════════════════════════════════════════
const css = `
.auth-page{position:relative;z-index:1;min-height:100vh;background:transparent;color:#F5F2EB;font-family:'Inter',system-ui,-apple-system,sans-serif;-webkit-font-smoothing:antialiased;display:flex;flex-direction:column;}

/* ══ Nav minimale contextuelle ══ */
.auth-nav{display:flex;justify-content:space-between;align-items:center;padding:20px 32px;max-width:1200px;margin:0 auto;width:100%;box-sizing:border-box;}
.auth-nav-logo{display:flex;align-items:center;gap:10px;text-decoration:none;color:#F5F2EB;}
.auth-nav-right{display:flex;align-items:center;gap:12px;}
.auth-logo-square{width:36px;height:36px;background:linear-gradient(135deg,#E8502A,#C73E1D);color:#FFF;border-radius:9px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:20px;box-shadow:0 4px 12px -2px rgba(232,80,42,.5);}
.auth-logo-name{font-weight:700;font-size:19px;letter-spacing:-0.3px;color:#F5F2EB;}
.auth-nav-cta{background:transparent;color:#F5F2EB;padding:10px 18px;border-radius:999px;text-decoration:none;font-weight:600;font-size:14px;border:1px solid rgba(237,234,227,.14);transition:all .15s ease;}
.auth-nav-cta:hover{background:rgba(255,255,255,.05);border-color:rgba(237,234,227,.28);}

/* ══ Layout centré une colonne ══ */
.auth-split{flex:1;display:flex;justify-content:center;align-items:flex-start;max-width:1200px;margin:0 auto;padding:24px 32px 64px;width:100%;box-sizing:border-box;}

.auth-form-col{display:flex;align-items:flex-start;justify-content:center;width:100%;}
.auth-form-inner{background:rgba(255,255,255,.025);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border-radius:24px;padding:42px 44px;width:100%;max-width:520px;border:1px solid rgba(237,234,227,.14);box-shadow:0 40px 80px -20px rgba(0,0,0,.4);box-sizing:border-box;}
.auth-form-register{max-width:560px;}
.auth-title{font-family:'Sora','Inter',sans-serif;font-size:28px;font-weight:700;letter-spacing:-0.6px;line-height:1.15;margin:0 0 8px;color:#F5F2EB;}
.auth-subtitle{font-size:15px;color:rgba(245,242,235,.62);margin:0 0 24px;line-height:1.5;}

/* Bug 2 audit UX — Récap plan choisi depuis /tarifs (?plan=…) */
.auth-plan-recap{display:flex;align-items:center;gap:14px;margin:0 0 28px;padding:16px 20px;border-radius:16px;background:linear-gradient(135deg,rgba(232,80,42,.12) 0%,rgba(232,80,42,.04) 100%);border:1px solid rgba(232,80,42,.3);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);}
.auth-plan-recap-icon{font-size:28px;line-height:1;flex-shrink:0;}
.auth-plan-recap-text{flex:1;min-width:0;}
.auth-plan-recap-title{font-size:15px;font-weight:600;color:#F5F2EB;line-height:1.3;margin-bottom:4px;}
.auth-plan-recap-title strong{color:#E8502A;font-weight:700;}
.auth-plan-recap-sub{font-size:13px;color:rgba(245,242,235,.62);line-height:1.3;}
@media (max-width:480px){
  .auth-plan-recap{padding:14px 16px;}
  .auth-plan-recap-icon{font-size:24px;}
  .auth-plan-recap-title{font-size:14px;}
}

/* ══ Indicateur 3 étapes — purement visuel ══ */
.auth-steps{display:flex;justify-content:space-between;align-items:center;gap:8px;margin:0 0 28px;padding:0;}
.auth-step{display:flex;flex-direction:column;align-items:center;gap:6px;flex:1;opacity:0.45;transition:opacity 0.2s;}
.auth-step.current,.auth-step.done{opacity:1;}
.auth-step-dot{width:10px;height:10px;border-radius:50%;background:rgba(237,234,227,.2);transition:background 0.2s,transform 0.2s,box-shadow 0.2s;}
.auth-step.current .auth-step-dot{background:#E8502A;transform:scale(1.25);box-shadow:0 0 12px rgba(232,80,42,.6);}
.auth-step.done .auth-step-dot{background:#7BE0A8;box-shadow:0 0 10px rgba(123,224,168,.4);}
.auth-step-label{font-size:11px;text-transform:uppercase;letter-spacing:0.07em;font-weight:600;color:rgba(245,242,235,.5);}
.auth-step.current .auth-step-label{color:#FF8A3D;}
.auth-step.done .auth-step-label{color:#7BE0A8;}

.auth-section-label{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#FF8A3D;margin:24px 0 14px;}
.auth-section-label:first-of-type{margin-top:4px;}

.auth-error{background:rgba(232,80,42,.12);color:#FFB199;padding:12px 16px;border-radius:10px;font-size:13px;margin-bottom:16px;border:1px solid rgba(232,80,42,.4);}

/* ══ Champs ══ */
.auth-field{margin-bottom:14px;}
.auth-field-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.auth-field-ville{position:relative;}
.auth-field label{display:block;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.07em;color:rgba(245,242,235,.62);margin-bottom:6px;}
.auth-field input,.auth-field select{width:100%;padding:12px 14px;border:1.5px solid rgba(237,234,227,.14);border-radius:10px;font-size:14px;font-family:inherit;color:#F5F2EB;outline:none;transition:border-color .15s,box-shadow .15s,background .15s;background:rgba(255,255,255,.04);box-sizing:border-box;color-scheme:dark;}
.auth-field input::placeholder{color:rgba(245,242,235,.4);}
.auth-field input:focus,.auth-field select:focus{border-color:#E8502A;box-shadow:0 0 0 4px rgba(232,80,42,.15);background:rgba(255,255,255,.06);}
.auth-field input:-webkit-autofill,
.auth-field input:-webkit-autofill:hover,
.auth-field input:-webkit-autofill:focus{-webkit-text-fill-color:#F5F2EB;-webkit-box-shadow:0 0 0 1000px rgba(19,17,15,.95) inset;transition:background-color 5000s ease-in-out 0s;caret-color:#F5F2EB;}
.auth-field-hint{font-size:12px;color:#7BE0A8;margin-top:5px;font-weight:500;}
.auth-field-hint-top{font-size:12px;color:rgba(245,242,235,.5);margin:-8px 0 10px;}

/* ══ Dropdown ville (autocomplete data.gouv) ══ */
.auth-suggestions{position:absolute;top:100%;left:0;right:0;margin-top:4px;background:rgba(19,17,15,.96);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border:1px solid rgba(237,234,227,.14);border-radius:10px;box-shadow:0 20px 50px -10px rgba(0,0,0,.6);z-index:20;overflow:hidden;max-height:240px;overflow-y:auto;}
.auth-suggestion{display:flex;align-items:center;justify-content:space-between;padding:11px 14px;cursor:pointer;font-size:14px;color:#F5F2EB;border-bottom:1px solid rgba(237,234,227,.08);transition:background 0.12s;}
.auth-suggestion:last-child{border-bottom:none;}
.auth-suggestion:hover{background:rgba(232,80,42,.15);}
.auth-suggestion-postcode{font-size:12px;color:rgba(245,242,235,.5);font-family:'JetBrains Mono','SF Mono',monospace;}
.auth-suggestion-context{font-size:12px;color:rgba(245,242,235,.55);margin-left:12px;text-align:right;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:55%;}

/* ══ CTA principal — gradient orange ══ */
.auth-btn{width:100%;padding:14px 18px;background:linear-gradient(135deg,#E8502A 0%,#FF6B3D 100%);color:#FFF;border:none;border-radius:10px;font-size:15px;font-weight:600;cursor:pointer;font-family:inherit;margin-top:8px;transition:all .15s;box-shadow:0 10px 30px -8px rgba(232,80,42,.55),inset 0 1px 0 rgba(255,255,255,.2);}
.auth-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 14px 36px -8px rgba(232,80,42,.65),inset 0 1px 0 rgba(255,255,255,.2);}
.auth-btn:disabled{opacity:.45;cursor:not-allowed;box-shadow:none;transform:none;}

/* ══ CTA secondaire (Recherche Google) — ghost dark ══ */
.auth-btn-secondary{width:100%;padding:11px;background:rgba(232,80,42,.08);color:#FF8A3D;border:1.5px solid rgba(232,80,42,.4);border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;transition:all .15s;}
.auth-btn-secondary:hover:not(:disabled){background:rgba(232,80,42,.15);border-color:#E8502A;}
.auth-btn-secondary:disabled{opacity:.4;cursor:not-allowed;}
.auth-link-btn{background:transparent;border:none;color:rgba(245,242,235,.62);font-size:12px;font-weight:500;cursor:pointer;text-decoration:underline;font-family:inherit;}
.auth-link-btn:hover{color:#FF8A3D;}

/* ══ Bandeau Google connectée ══ */
.auth-google-connected{background:rgba(46,160,90,.12);border:1px solid rgba(46,160,90,.35);border-radius:10px;padding:12px 14px;font-size:13px;color:#7BE0A8;display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:14px;}
.auth-google-connected strong{color:#A8F0C8;}

/* ══ Résultats Google Places ══ */
.auth-google-results{display:flex;flex-direction:column;gap:6px;margin-top:10px;}
.auth-google-result{padding:12px 14px;border:1.5px solid rgba(237,234,227,.14);border-radius:10px;cursor:pointer;transition:all 0.15s;background:rgba(255,255,255,.03);}
.auth-google-result:hover{border-color:#E8502A;background:rgba(232,80,42,.1);}
.auth-google-name{font-size:14px;font-weight:700;color:#F5F2EB;}
.auth-google-addr{font-size:12px;color:rgba(245,242,235,.55);margin-top:2px;}
.auth-google-meta{font-size:12px;color:#F5F2EB;margin-top:6px;display:flex;gap:6px;}
.auth-muted{color:rgba(245,242,235,.45);}
.auth-google-err{font-size:13px;color:#FFB199;margin-top:8px;background:rgba(232,80,42,.12);padding:8px 12px;border-radius:8px;border:1px solid rgba(232,80,42,.35);}
.auth-skip{text-align:center;margin-top:10px;}
.auth-skip span{font-size:12px;color:rgba(245,242,235,.55);cursor:pointer;text-decoration:underline;}
.auth-skip span:hover{color:#FF8A3D;}

/* ══ CGV ══ */
.auth-cgv{margin:20px 0 6px;}
.auth-cgv-label{display:flex;align-items:flex-start;gap:10px;font-size:13px;color:#F5F2EB;line-height:1.5;cursor:pointer;}
.auth-cgv-label input[type="checkbox"]{margin-top:3px;width:16px;height:16px;accent-color:#E8502A;flex-shrink:0;cursor:pointer;}
.auth-cgv-label a{color:#FF8A3D;text-decoration:underline;font-weight:600;}
.auth-cgv-label a:hover{color:#FFA869;}
.auth-cgv-note{font-size:11.5px;color:rgba(245,242,235,.5);margin:8px 0 0 26px;line-height:1.5;}
.auth-cgv-note a{color:#FF8A3D;text-decoration:underline;}
.auth-cgv-hint{font-size:12px;color:#FFB199;margin-top:6px;text-align:center;}

.auth-switch{text-align:center;margin-top:22px;font-size:14px;color:rgba(245,242,235,.62);}
.auth-link-primary{color:#FF8A3D;text-decoration:none;font-weight:600;}
.auth-link-primary:hover{text-decoration:underline;}

@media (max-width:768px){
  .auth-nav{padding:16px 20px;}
  .auth-nav-cta{padding:8px 14px;font-size:13px;}
  .auth-split{padding:16px 20px 32px;}
  .auth-form-inner{padding:28px 22px;max-width:none;}
  .auth-title{font-size:24px;}
  .auth-subtitle{font-size:14px;margin-bottom:20px;}
  .auth-field-row{grid-template-columns:1fr;gap:0;}
  .auth-steps{margin-bottom:22px;}
  .auth-step-label{font-size:10px;}
}
`;
